/* KAZNE AETR → tab KAZNE — evidencija kazni vozača.
   Raspored prati Administracija → Kartice: forma levo, tabela desno,
   pretraga po svemu i izvoz u Excel. */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../custom/customDropdown';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loading from '../../Loading/loading';
import { AuthContext } from '../../Header/auth - context';
import useVisinaTabele from "../../custom/visinaTabele";
import './Kazne.css';

const praznaForma = {
    id_zaposleni: null,
    id_vozilo: null,
    id_tip_kazne: null,
    DatumKazne: null,
    DatumPlacanja: null,
    id_Drzava: null,
    Iznos: null,
    id_Valuta: null,
    id_TipSudskogPostupka: null,
    id_StatusSudskogPostupka: null,
    Napomena: '',
};

/* Link do fajla — ista konvencija kao u Administraciji (unos vozila). */
const putanjaFajla = (p) => {
    if (!p) return null;
    const norm = String(p).replace(/\\/g, '/').replace(/^C:\/Dokumenta\//, '');
    return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(norm.replace(/^\//, ''))}`;
};

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    if (isNaN(x.getTime())) return null;
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 10);
};

/* 'dd.MM.yyyy.' iz SP-a → Date, da se forma popuni pri izmeni */
const izSrpskogDatuma = (s) => {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(String(s || ''));
    return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
};

const KazneEvidencija = () => {
    const { t } = useTranslation();
    const toast = useRef(null);
    const fileRef = useRef(null);
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);

    const [load, setLoad] = useState(false);
    const [vozaci, setVozaci] = useState([]);
    const [tipovi, setTipovi] = useState([]);
    const [drzave, setDrzave] = useState([]);
    const [valute, setValute] = useState([]);
    const [vozila, setVozila] = useState([]);
    const [tipoviSud, setTipoviSud] = useState([]);
    const [statusiSud, setStatusiSud] = useState([]);
    const [podaci, setPodaci] = useState([]);
    const [formData, setFormData] = useState(praznaForma);
    const [selectedId, setSelectedId] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [tipDialog, setTipDialog] = useState(false);
    const [noviTip, setNoviTip] = useState('');
    const [tipZaIzmenu, setTipZaIzmenu] = useState(null);
    const [fajlovi, setFajlovi] = useState([]);       // izabrani, još neposlati
    /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
    const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([podaci]);

    const poziv = async (step, body = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/kazne', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step, ...body }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'Greška');
        return r.json();
    };

    const ucitaj = async () => {
        setLoad(true);
        try {
            const data = await poziv('INIT');
            /* SP vraća jedan JSON string u prvoj koloni prvog reda */
            const raw = Array.isArray(data) && data.length ? Object.values(data[0])[0] : null;
            const parsed = raw ? JSON.parse(raw) : {};
            setVozaci(parsed.VozaciDB || []);
            setTipovi(parsed.TipoviDB || []);
            setDrzave(parsed.DrzaveDB || []);
            setValute(parsed.ValuteDB || []);
            setVozila(parsed.VozilaDB || []);
            setTipoviSud(parsed.TipoviSudDB || []);
            setStatusiSud(parsed.StatusiSudDB || []);
            setPodaci(parsed.PodaciDB || []);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        }
        setLoad(false);
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /* Klik na "Izaberi" u redu — ponovni klik na isti red poništava izbor */
    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    id_zaposleni: row.id_zaposleni ?? null,
                    id_vozilo: row.id_vozilo ?? null,
                    id_tip_kazne: row.id_tip_kazne ?? null,
                    DatumKazne: izSrpskogDatuma(row.DatumKazne),
                    DatumPlacanja: izSrpskogDatuma(row.DatumPlacanja),
                    id_Drzava: row.id_Drzava ?? null,
                    Iznos: row.Iznos != null ? Number(row.Iznos) : null,
                    id_Valuta: row.id_Valuta ?? null,
                    id_TipSudskogPostupka: row.id_TipSudskogPostupka ?? null,
                    id_StatusSudskogPostupka: row.id_StatusSudskogPostupka ?? null,
                    Napomena: row.Napomena || '',
                });
            } else {
                setFormData(praznaForma);
            }
            setFajlovi([]);
            if (fileRef.current) fileRef.current.value = '';
            return next;
        });
    };

    const posaljiFajlove = async (idKazne) => {
        if (!fajlovi.length || !idKazne) return;
        const fd = new FormData();
        fd.append('id', idKazne);                 // mora PRE fajlova (multer čita redom)
        fd.append('entityType', 'tahokazna');
        fajlovi.forEach(f => fd.append('dokumenta', f));
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/kazneFiles', {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + auth.token },
            body: fd,
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'Greška pri slanju fajlova');
    };

    const sacuvaj = async () => {
        if (!formData.id_zaposleni) {
            toast.current?.show({ severity: 'warn', summary: t('common.upozorenje'), detail: t('kazneEv.vozacObavezan'), life: 3000 });
            return;
        }
        if (formData.id_TipSudskogPostupka && !formData.id_StatusSudskogPostupka) {
            toast.current?.show({ severity: 'warn', summary: t('common.upozorenje'), detail: t('kazneEv.statusSudObavezan'), life: 4000 });
            return;
        }
        setLoad(true);
        try {
            const res = await poziv('SAVE_KAZNA', {
                id: selectedId,
                formData: {
                    ...formData,
                    DatumKazne: toUtcIso(formData.DatumKazne),
                    DatumPlacanja: toUtcIso(formData.DatumPlacanja),
                },
            });
            const red = Array.isArray(res) && res.length ? res[0] : {};
            const idKazne = red.ID || selectedId;
            await posaljiFajlove(idKazne);

            toast.current?.show({ severity: 'success', summary: t('common.sacuvano'), detail: red.message || '', life: 3000 });
            setFormData(praznaForma);
            setSelectedId(null);
            setFajlovi([]);
            if (fileRef.current) fileRef.current.value = '';
            await ucitaj();
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        }
        setLoad(false);
    };

    const obrisi = () => {
        if (!selectedId) return;
        confirmDialog({
            message: t('kazneEv.potvrdaBrisanja'),
            header: t('common.potvrda'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('common.da'),
            rejectLabel: t('common.ne'),
            accept: async () => {
                setLoad(true);
                try {
                    const res = await poziv('DELETE_KAZNA', { id: selectedId });
                    const red = Array.isArray(res) && res.length ? res[0] : {};
                    toast.current?.show({ severity: 'success', summary: t('common.sacuvano'), detail: red.message || '', life: 3000 });
                    setSelectedId(null);
                    setFormData(praznaForma);
                    await ucitaj();
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
                }
                setLoad(false);
            },
        });
    };

    /* "+" pored dropdowna tipa kazne otvara mali šifarnik — unos, izmena i
       brisanje, isti obrazac kao novi artikl u prijemnici. Dijalog ostaje
       otvoren posle snimanja da bi se moglo dodati/menjati više stavki. */
    const zatvoriTipDialog = () => {
        setTipDialog(false);
        setTipZaIzmenu(null);
        setNoviTip('');
    };

    const sacuvajTip = async () => {
        if (!noviTip.trim()) return;
        try {
            const res = await poziv('SAVE_TIP', {
                formData: { Naziv: noviTip.trim(), id_tip: tipZaIzmenu },
            });
            const red = Array.isArray(res) && res.length ? res[0] : {};
            toast.current?.show({ severity: 'success', summary: t('common.sacuvano'), detail: red.message || '', life: 3000 });
            const noviId = red.ID ? Number(red.ID) : null;
            const bilaIzmena = !!tipZaIzmenu;
            setNoviTip('');
            setTipZaIzmenu(null);
            await ucitaj();
            // novi tip se odmah bira u formi; kod izmene se izbor ne dira
            if (!bilaIzmena && noviId) setFormData(prev => ({ ...prev, id_tip_kazne: noviId }));
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        }
    };

    const obrisiTip = (tip) => {
        confirmDialog({
            message: `${t('kazneEv.potvrdaBrisanjaTipa')} „${tip.label}"?`,
            header: t('common.potvrda'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('common.da'),
            rejectLabel: t('common.ne'),
            accept: async () => {
                try {
                    const res = await poziv('DELETE_TIP', { formData: { id_tip: tip.value } });
                    const red = Array.isArray(res) && res.length ? res[0] : {};
                    /* SP odbija brisanje tipa koji se koristi na postojećim kaznama */
                    const koriscen = String(red.message || '').includes('koristi');
                    toast.current?.show({
                        severity: koriscen ? 'warn' : 'success',
                        summary: koriscen ? t('common.upozorenje') : t('common.sacuvano'),
                        detail: red.message || '', life: 4000,
                    });
                    if (!koriscen) {
                        if (String(formData.id_tip_kazne) === String(tip.value)) {
                            setFormData(prev => ({ ...prev, id_tip_kazne: null }));
                        }
                        if (String(tipZaIzmenu) === String(tip.value)) { setTipZaIzmenu(null); setNoviTip(''); }
                        await ucitaj();
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
                }
            },
        });
    };

    const KOLONE = [
        { field: 'Vozac',          header: t('ture.vozac') },
        { field: 'Vozilo',         header: t('nova.vozilo') },
        { field: 'TipKazne',       header: t('kazneEv.tipKazne') },
        { field: 'DatumKazne',     header: t('kazneEv.datumKazne') },
        { field: 'DatumPlacanja',  header: t('kazneEv.datumPlacanja') },
        { field: 'Drzava',         header: t('kazneEv.drzava') },
        { field: 'Iznos',          header: t('kazneEv.iznos') },
        { field: 'Valuta',         header: t('kazneEv.valuta') },
        { field: 'TipSudskogPostupka',    header: t('kazneEv.tipSud') },
        { field: 'StatusSudskogPostupka', header: t('kazneEv.statusSud') },
        { field: 'StatusPlacanja', header: t('taho.status') },
        { field: 'Napomena',       header: t('kazneEv.napomena') },
        /* MB #139 — kazne koje vozač slika sa telefona: kategorija i tura */
        { field: 'Kategorija',     header: t('kazneEv.kategorija') },
        { field: 'Tura',           header: t('kazneEv.tura') },
    ];

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Kazne');
        const headers = KOLONE.map(c => c.header);
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (podaci || []).forEach(r => {
            const row = worksheet.addRow(KOLONE.map(c => r[c.field] ?? ''));
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach((col, i) => {
            const lengths = [headers[i].length, ...(podaci || []).map(r => String(r[KOLONE[i].field] ?? '').length)];
            col.width = Math.max(...lengths) + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
        }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `Kazne - ${company?.companyName ?? ''} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded
                    onClick={obrisi} data-pr-tooltip={t('kazneEv.obrisi')} />}
            </div>
            <input type="search" placeholder={t('common.pretraga')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    const fajloviBody = (row) => {
        const lista = String(row.Fajlovi || '').split('|').filter(Boolean);
        if (!lista.length) return '—';
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {lista.map((p, i) => (
                    <Button key={i} label={p.split('/').pop()} icon="pi pi-eye"
                        className="p-button-text p-button-sm kazne-fajl-link"
                        onClick={() => window.open(putanjaFajla(p), '_blank')}
                        tooltip={t('taho.pregledajDokument')} tooltipOptions={{ position: 'top' }} />
                ))}
            </div>
        );
    };

    return (
        <div style={{ marginTop: '1%' }}>
            {load && <Loading />}
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="col-12">
                <div className="blagajnaDashboard dnevniceDashboard">
                    {/* ── forma ── */}
                    <Card>
                        <form autoComplete="off">
                            <div className="kazne-forma-grid">
                                <div>
                                    <label htmlFor="id_zaposleni">{t('ture.vozac')}:</label>
                                    <CustomDropdown
                                        id="id_zaposleni" name="id_zaposleni"
                                        value={formData.id_zaposleni}
                                        options={vozaci}
                                        onChange={handleChange}
                                        optionLabel="label"
                                        placeholder={t('kazneEv.izaberiVozaca')}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="id_vozilo">{t('nova.vozilo')}:</label>
                                    <CustomDropdown
                                        id="id_vozilo" name="id_vozilo"
                                        value={formData.id_vozilo}
                                        options={vozila}
                                        onChange={handleChange}
                                        optionLabel="label"
                                        placeholder={t('taho.izaberiVozilo')}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="id_tip_kazne">{t('kazneEv.tipKazne')}:</label>
                                    <div className="fakture-stavke-radnje">
                                        <CustomDropdown
                                            id="id_tip_kazne" name="id_tip_kazne"
                                            value={formData.id_tip_kazne}
                                            options={tipovi}
                                            onChange={handleChange}
                                            optionLabel="label"
                                            placeholder={t('kazneEv.izaberiTip')}
                                            style={{ flex: 1, minWidth: 0 }}
                                        />
                                        <Button type="button" icon="pi pi-plus" severity="info"
                                            className="kazne-tip-plus"
                                            onClick={() => { setNoviTip(''); setTipZaIzmenu(null); setTipDialog(true); }}
                                            data-pr-tooltip={t('kazneEv.noviTip')} />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="DatumKazne">{t('kazneEv.datumKazne')}:</label>
                                    <Calendar id="DatumKazne" name="DatumKazne"
                                        value={formData.DatumKazne}
                                        onChange={handleChange}
                                        showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                                </div>

                                <div>
                                    <label htmlFor="DatumPlacanja">{t('kazneEv.datumPlacanja')}:</label>
                                    <Calendar id="DatumPlacanja" name="DatumPlacanja"
                                        value={formData.DatumPlacanja}
                                        onChange={handleChange}
                                        showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                                </div>

                                <div>
                                    <label htmlFor="id_Drzava">{t('kazneEv.drzava')}:</label>
                                    <CustomDropdown
                                        id="id_Drzava" name="id_Drzava"
                                        value={formData.id_Drzava}
                                        options={drzave}
                                        onChange={handleChange}
                                        optionLabel="label"
                                        placeholder={t('kazneEv.izaberiDrzavu')}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                {/* Iznos i valuta idu zajedno u jednoj ćeliji */}
                                <div>
                                    <label htmlFor="Iznos">{t('kazneEv.iznos')}:</label>
                                    <div className="fakture-stavke-radnje">
                                        <InputNumber
                                            id="Iznos" name="Iznos"
                                            value={formData.Iznos}
                                            onValueChange={e => setFormData(prev => ({ ...prev, Iznos: e.value }))}
                                            mode="decimal" minFractionDigits={2} maxFractionDigits={2}
                                            locale="sr-RS"
                                            style={{ flex: 1, minWidth: 0 }}
                                            inputStyle={{ width: '100%', textAlign: 'right' }}
                                        />
                                        <CustomDropdown
                                            id="id_Valuta" name="id_Valuta"
                                            value={formData.id_Valuta}
                                            options={valute}
                                            onChange={handleChange}
                                            optionLabel="label"
                                            placeholder={t('kazneEv.valuta')}
                                            className="kazne-valuta-dd"
                                        />
                                    </div>
                                </div>

                                {/* Više dokumenata — isti obrazac kao Administracija → Klijenti → Unos */}
                                <div>
                                    <label htmlFor="dokumenta">{t('ture.viseDokumenata')}:</label>
                                    {fajlovi.length === 0 ? (
                                        <FileUpload
                                            name="dokumenta"
                                            chooseLabel={t('kazneEv.izaberi')}
                                            customUpload={false}
                                            multiple
                                            auto={false}
                                            mode="basic"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            maxFileSize={5000000}
                                            onSelect={(e) => setFajlovi(Array.from(e.files || []))}
                                        />
                                    ) : (
                                        <div>
                                            {fajlovi.map((file, index) => (
                                                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                    <span style={{ marginRight: '1%', flexGrow: 1 }}>{file.name}</span>
                                                    <Button
                                                        icon="pi pi-times"
                                                        type="button"
                                                        className="p-button-rounded p-button-danger p-button-text"
                                                        onClick={() => setFajlovi(prev => prev.filter((_, i) => i !== index))}
                                                        tooltip={t('kazneEv.ukloniFajl')}
                                                        tooltipOptions={{ position: 'top' }}
                                                    />
                                                </div>
                                            ))}
                                            <Button
                                                label={t('ture.obrisiSve')}
                                                type="button"
                                                onClick={() => setFajlovi([])}
                                                className="p-button-text p-button-danger"
                                                style={{ marginTop: '1rem' }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Sudski postupak — oba mogu ostati prazna, ali čim se izabere
                                    tip, status postaje obavezan (isto proverava i procedura). */}
                                <div>
                                    <label htmlFor="id_TipSudskogPostupka">{t('kazneEv.tipSud')}:</label>
                                    <CustomDropdown
                                        id="id_TipSudskogPostupka" name="id_TipSudskogPostupka"
                                        value={formData.id_TipSudskogPostupka}
                                        options={tipoviSud}
                                        onChange={e => setFormData(prev => ({
                                            ...prev,
                                            id_TipSudskogPostupka: e.target.value,
                                            // brisanjem tipa nema smisla da status ostane
                                            id_StatusSudskogPostupka: e.target.value ? prev.id_StatusSudskogPostupka : null,
                                        }))}
                                        optionLabel="label"
                                        placeholder={t('kazneEv.izaberiTipSud')}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="id_StatusSudskogPostupka">
                                        {t('kazneEv.statusSud')}{formData.id_TipSudskogPostupka ? ' *' : ''}:
                                    </label>
                                    <CustomDropdown
                                        id="id_StatusSudskogPostupka" name="id_StatusSudskogPostupka"
                                        value={formData.id_StatusSudskogPostupka}
                                        options={statusiSud}
                                        onChange={handleChange}
                                        optionLabel="label"
                                        placeholder={t('kazneEv.izaberiStatusSud')}
                                        disabled={!formData.id_TipSudskogPostupka}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                <div className="kazne-polje-2-3">
                                    <label htmlFor="Napomena">{t('kazneEv.napomena')}:</label>
                                    <InputTextarea id="Napomena" name="Napomena" rows={3}
                                        value={formData.Napomena} onChange={handleChange} />
                                </div>
                            </div>

                            <div className="kazne-forma-akcije">
                                <button type="button" onClick={sacuvaj} className="btn btn-success">
                                    {selectedId ? t('common.sacuvaj') : t('kazneEv.unos')}
                                </button>
                            </div>
                        </form>
                    </Card>

                    {/* ── tabela ── */}
                    <Card>
                        <Tooltip target=".export-buttons>button" position="bottom" />
                        <div ref={omotTabele}>
                        <DataTable
                            value={podaci || []}
                            paginator rows={redovaTabele}
                            globalFilter={globalFilter}
                            scrollable scrollHeight={visinaTabele}
                            header={tableHeader}
                            emptyMessage={t('common.noDataTable')}
                            resizableColumns columnResizeMode="expand"
                            className="turePregledTable stavke-tabela"
                            rowClassName={r => (String(r.id) === String(selectedId) ? 'selected-row' : '')}
                        >
                            <Column header={t('kazneEv.izaberi')} style={{ minWidth: '90px' }}
                                body={rowData => (
                                    <Button label={t('kazneEv.izaberi')} icon="pi pi-eye" type="button"
                                        className="p-button-text p-button-sm"
                                        onClick={() => handleRowSelect(rowData)} />
                                )} />
                            <Column field="izvor" header={t('kazneEv.izvor')} sortable style={{ minWidth: '110px' }}
                                body={r => (r.izvor === 'MOBILNI'
                                    ? <span className="kazne-izvor-telefon"><i className="pi pi-mobile" /> {t('kazneEv.izvorTelefon')}</span>
                                    : <span className="kazne-izvor-web">{t('kazneEv.izvorKancelarija')}</span>)} />
                            {KOLONE.map(c => (
                                <Column key={c.field} field={c.field} header={c.header} sortable
                                    style={{ minWidth: '140px' }}
                                    body={r => r[c.field] || '—'} />
                            ))}
                            <Column header={t('kazneEv.fajl')} style={{ minWidth: '220px' }} body={fajloviBody} />
                        </DataTable>
                        </div>
                    </Card>
                </div>
            </div>

            {/* ── šifarnik tipova kazni: unos, izmena i brisanje ── */}
            <Dialog header={t('kazneEv.tipoviNaslov')} visible={tipDialog} style={{ width: '38rem' }}
                onHide={zatvoriTipDialog}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <label htmlFor="noviTip">{t('kazneEv.nazivTipa')}:</label>
                    <InputText id="noviTip" value={noviTip} autoFocus
                        onChange={e => setNoviTip(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sacuvajTip(); } }} />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        {tipZaIzmenu && (
                            <Button label={t('kazneEv.odustani')} type="button" className="p-button-text"
                                onClick={() => { setTipZaIzmenu(null); setNoviTip(''); }} />
                        )}
                        <Button label={tipZaIzmenu ? t('common.sacuvaj') : t('kazneEv.dodaj')}
                            icon={tipZaIzmenu ? 'pi pi-check' : 'pi pi-plus'} type="button"
                            onClick={sacuvajTip} />
                    </div>

                    <DataTable value={tipovi || []} size="small" scrollable scrollHeight="18rem"
                        emptyMessage={t('kazneEv.nemaTipova')} className="turePregledTable stavke-tabela"
                        rowClassName={r => (String(r.value) === String(tipZaIzmenu) ? 'selected-row' : '')}>
                        <Column field="label" header={t('kazneEv.nazivTipa')} sortable />
                        <Column header="" style={{ width: '110px' }} body={r => (
                            <div className="fakture-stavke-radnje">
                                <Button icon="pi pi-pencil" type="button" rounded text
                                    tooltip={t('kazneEv.izmeni')} tooltipOptions={{ position: 'top' }}
                                    onClick={() => { setTipZaIzmenu(r.value); setNoviTip(r.label); }} />
                                <Button icon="pi pi-trash" type="button" rounded text severity="danger"
                                    tooltip={t('kazneEv.obrisi')} tooltipOptions={{ position: 'top' }}
                                    onClick={() => obrisiTip(r)} />
                            </div>
                        )} />
                    </DataTable>
                </div>
            </Dialog>
        </div>
    );
};

export default KazneEvidencija;
